import React, { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Result } from 'result'
import { delay } from 'utils'
import { ChartDataLoader } from 'uiComponents/loaders'
import { InlineDataTable, HeaderRow, TableHeader, DataRow, Cell } from 'uiComponents/table'
import { NestedContainerWrapper } from 'uiComponents/table/nestedContainer'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import Infotip from 'uiComponents/infotip'
import { useGetAllArticles, useGetAllCategories, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { useGetAllArticlesQuery, useGetAllCategoriesQuery } from 'products/reduxApi'
import { useUrlParam } from 'utils/useUrlParam'
import { useNavigation } from 'hocs'
import { LIST, TIME_SLOTS } from 'capacity/copy'
import { useTranslation } from 'translations'
import { actionColumnWidth } from 'uiComponents/table/actionColumnWidth'
import { useGetTimeSlotGroupsQuery } from './reduxQuery'
import { TimeSlotsServiceContext } from './context'
import { TimeSlotGroup, TimeSlotsValidationError } from './timeSlotsService'
import { TimeSlotsListPageProps } from './list'
import { TimeSlotsList } from './listBody'
import { selectionLabel, TimeSlotsToolbar, TimeSlotsView, useSearchOptions } from './listToolbar'
import {
    StatusFilter,
    effectiveStatus,
    groupsThatApply,
    legacySelection,
    readSelection,
    readStatusFilter,
    sortGroups,
    statusCounts,
} from './domain/listFilters'

const SKELETON_ROWS = [0, 1, 2]

export function TimeSlotsListRedesign(props: TimeSlotsListPageProps) {
    const { t } = useTranslation()
    const timeSlotsService = React.useContext(TimeSlotsServiceContext)
    const [updating, setUpdating] = useState(false)
    const [expandedElement, setExpandedElement] = useState('')
    const groupsQuery = useGetTimeSlotGroupsQuery(props.accountSlug)
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const catalogueLoading = useIsLoadingCategoriesOrArticles()
    // The skeleton is for the first load only: a background refetch keeps the rows on screen.
    const catalogueFirstLoad =
        useGetAllArticlesQuery({ account: props.accountSlug }).isLoading ||
        useGetAllCategoriesQuery({ account: props.accountSlug }).isLoading
    const searchOptions = useSearchOptions()
    const productsFeature = props.hasFeature('TimeSlotsForProduct', props.accountSlug)

    const navigation = useNavigation()
    const [viewParam] = useUrlParam('timeslots_view')
    const [productParam] = useUrlParam('product')
    const [categoryParam] = useUrlParam('category')
    const [statusParam] = useUrlParam('status')
    const [legacyFilter] = useUrlParam('filter')
    const view: TimeSlotsView = productsFeature && viewParam === 'products' ? 'products' : 'categories'
    const productsView = view === 'products'
    const selection = readSelection({ product: productParam, category: categoryParam })
    const status = readStatusFilter(statusParam)

    useEffect(() => {
        if (!legacyFilter) {
            return
        }
        const translated = legacySelection(legacyFilter, productsView)
        navigation.addQueryWithReplace({
            filter: undefined,
            product: translated?.kind === 'product' ? translated.id : undefined,
            category: translated?.kind === 'category' ? translated.id : undefined,
        })
    }, [legacyFilter])

    useEffect(() => {
        const viewTitle = productsView ? t('timeSlots.list.titleProducts') : t('timeSlots.list.titleCategories')
        const title = productsFeature ? viewTitle : t('timeSlots.list.title')
        props.setActiveSection('timeSlots', title)
    }, [productsView])

    // Read on every render so a page left open past midnight catches up. The account's venue timezone is not
    // known to the dashboard, so this is the browser's calendar day.
    const todayISO = format(new Date(), 'yyyy-MM-dd')
    const groups = useMemo<TimeSlotGroup[]>(
        () =>
            (groupsQuery.data?.groups ?? []).map((group) => ({
                ...group,
                status: effectiveStatus(group, todayISO),
                productsListNames:
                    group.productsLists.length === 0
                        ? t('timeSlots.list.defaultForAllCategories')
                        : group.productsLists.map((pl) => pl.name).join(', '),
            })),
        [groupsQuery.data, todayISO],
    )
    const flattenedCategories = useMemo(() => groups.flatMap((group) => group.productsLists), [groups])

    const scoped = useMemo(() => {
        if (selection) {
            return groupsThatApply(selection, groups, articles, categories)
        }
        return groups.filter((group) => !!group.article === productsView)
    }, [groups, selection?.kind, selection?.id, articles, categories, productsView])
    const counts = useMemo(() => statusCounts(scoped), [scoped])
    const visible = useMemo(
        () => sortGroups(scoped.filter((group) => status === 'all' || group.status === status)),
        [scoped, status],
    )

    const onSelect = (next: typeof selection) =>
        navigation.addQueryWithReplace({
            product: next?.kind === 'product' ? next.id : undefined,
            category: next?.kind === 'category' ? next.id : undefined,
        })
    const onViewChange = (next: TimeSlotsView) =>
        navigation.addQueryWithReplace({
            timeslots_view: next === 'categories' ? undefined : next,
            product: undefined,
            category: undefined,
        })
    const onStatusChange = (next: StatusFilter) =>
        navigation.addQueryWithReplace({ status: next === 'all' ? undefined : next })
    const showAll = () => navigation.addQueryWithReplace({ product: undefined, category: undefined, status: undefined })

    const onPriorityUpdateConfirm = async (priority: string, id: string) => {
        try {
            setUpdating(true)
            const response: Result<TimeSlotGroup, TimeSlotsValidationError> =
                await timeSlotsService.updateTimeSlotGroupPriority(props.accountSlug, Number(priority), id)
            response
                .ifFailure((err) => {
                    setUpdating(false)
                    props.replaceMessages(
                        err.code,
                        'error',
                        t('timeSlots.list.errorWithMessage', { message: err.message }),
                    )
                })
                .ifSuccess(async () => {
                    await groupsQuery.refetch()
                    setUpdating(false)
                    props.replaceMessages('success', 'success', t('timeSlots.list.prioritySaveSuccess'))
                    await delay(3000)
                    props.removeAllMessages()
                })
        } catch {
            setUpdating(false)
            props.replaceMessages('server_error', 'error', t('timeSlots.list.prioritySaveError'))
        }
    }

    const loading = groupsQuery.isLoading || (!!selection && catalogueFirstLoad)
    const selectionName = selectionLabel(selection, searchOptions)
    const emptyText = (() => {
        if (groupsQuery.isError) {
            return TIME_SLOTS.failed
        }
        if (groups.length === 0) {
            return TIME_SLOTS.none
        }
        if (selection && selectionName === null && !catalogueLoading) {
            return TIME_SLOTS.emptyForUnknown
        }
        const statusLabel = status === 'all' ? null : TIME_SLOTS.status[status]
        if (selection && statusLabel) {
            return TIME_SLOTS.emptyForBoth(statusLabel, selectionName ?? '')
        }
        if (selection) {
            return TIME_SLOTS.emptyForSelection(selectionName ?? '')
        }
        return statusLabel ? TIME_SLOTS.emptyForStatus(statusLabel) : TIME_SLOTS.none
    })()

    const dropDownColumn = productsFeature ? ['2.3em'] : []
    const columnWidths = [...dropDownColumn, null, null, '6em', '9.7em', '9.3em', actionColumnWidth(t('common.edit'))]
    const nested = productsView && !selection

    const list = (
        <TimeSlotsList
            accountSlug={props.accountSlug}
            timeSlots={visible}
            flattenedCategories={flattenedCategories}
            expandedElement={expandedElement}
            productsView={productsView}
            setExpandedTimeSlot={(id) => setExpandedElement(expandedElement === id ? '' : id)}
            onPriorityUpdateConfirm={onPriorityUpdateConfirm}
            hasPermission={props.hasPermission}
            hasFeature={props.hasFeature}
            redesign
            describeTarget={!!selection}
        />
    )

    return (
        <>
            <TimeSlotsToolbar
                options={searchOptions}
                optionsLoading={catalogueLoading}
                selection={selection}
                onSelect={onSelect}
                view={view}
                onViewChange={onViewChange}
                showViewSwitch={productsFeature}
                viewBusy={groupsQuery.isLoading || updating}
                status={status}
                onStatusChange={onStatusChange}
                counts={counts}
            />
            <InlineDataTable columnWidths={columnWidths} bordered id="time-slots-list">
                <HeaderRow>
                    {productsFeature && <TableHeader noPadding />}
                    <TableHeader>{t('timeSlots.list.columns.name')}</TableHeader>
                    <TableHeader>
                        {selection
                            ? TIME_SLOTS.appliesTo
                            : productsView
                            ? t('timeSlots.list.columns.product')
                            : t('timeSlots.list.columns.categories')}
                    </TableHeader>
                    <TableHeader style={{ paddingLeft: '0.8em' }}>
                        {t('timeSlots.list.columns.priority')}
                        <Infotip pointer="right" maxWidth="25em">
                            {t('timeSlots.list.priorityTooltip')}
                        </Infotip>
                    </TableHeader>
                    <TableHeader>{t('timeSlots.list.columns.weeklyPattern')}</TableHeader>
                    <TableHeader>{t('timeSlots.list.columns.dateRange')}</TableHeader>
                    <TableHeader nonInteractive />
                </HeaderRow>
                {updating && <ChartDataLoader />}
                {loading &&
                    SKELETON_ROWS.map((index) => (
                        <DataRow key={index} aria-hidden="true">
                            {productsFeature && <Cell noPadding />}
                            <Cell>
                                <SkeletonBar width="12rem" height="0.85rem" />
                            </Cell>
                            <Cell>
                                <SkeletonBar width="7rem" height="0.85rem" />
                            </Cell>
                            <Cell>
                                <SkeletonBar width="2.5rem" height="0.85rem" />
                            </Cell>
                            <Cell>
                                <SkeletonBar width="6rem" height="0.85rem" />
                            </Cell>
                            <Cell>
                                <SkeletonBar width="6rem" height="0.85rem" />
                            </Cell>
                            <Cell />
                        </DataRow>
                    ))}
                {!loading && visible.length === 0 && (
                    <div className="timeslots-empty" role={groupsQuery.isError ? 'alert' : undefined}>
                        <p>{emptyText}</p>
                        {groupsQuery.isError ? (
                            <button type="button" className="timeslots-show-all" onClick={() => groupsQuery.refetch()}>
                                {LIST.retry}
                            </button>
                        ) : (
                            groups.length > 0 && (
                                <button type="button" className="timeslots-show-all" onClick={showAll}>
                                    {TIME_SLOTS.showAll}
                                </button>
                            )
                        )}
                    </div>
                )}
                {!loading && visible.length > 0 && !nested && list}
                {!loading && visible.length > 0 && nested && (
                    <NestedContainerWrapper flat grade={0} className="expanded">
                        {list}
                    </NestedContainerWrapper>
                )}
            </InlineDataTable>
        </>
    )
}
